<template>
	<v-sheet rounded>
		<v-card color="#125B92">
			<v-card-title v-if="titleBar" class="align-center">
				<v-icon left large color="#E3ECF2">mdi-account-group</v-icon>
				<h3 class="white--text">{{ title }}</h3>
				<v-spacer />
				<slot />
			</v-card-title>

			<v-divider v-if="titleBar" class="thick" />

			<v-data-table
				:headers="tableHeaders"
				:items="items"
				:loading="loading"
				:items-per-page.sync="filters.paginated"
				:footer-props="{
					'items-per-page-options': [10, 20, 30, 40, 50]
				}"
				:page.sync="filters.page"
				:server-items-length="totalUsers"
				:sort-by.sync="filters.sort"
				:sort-desc.sync="filters.descending"
				class="table-stripped"
				@update:page="$vuetify.goTo(0)">
				<template #top>
					<v-toolbar flat>
						<v-spacer />
						<div class="flex-fill datatable-filter-large">
							<v-text-field
								v-model.trim="filters.search"
								class="table-search"
								append-icon="mdi-magnify"
								placeholder="Search..."
								name="search"
								autocomplete="off"
								single-line
								clearable
								filled
								dense
								outlined
								full-width
								autofocus
								hide-details />
						</div>
						<template v-if="! simplify">
							<div class="ml-3 datatable-filter-small">
								<v-select
									v-model="filters.type"
									:items="types"
									placeholder="Filter by Type"
									item-text="name"
									item-value="id"
									single-line
									clearable
									filled
									dense
									outlined
									hide-details></v-select>
							</div>
							<div class="ml-3 datatable-filter-small">
								<v-select
									v-model="filters.status"
									:items="statuses"
									placeholder="Filter by Status"
									item-text="name"
									item-value="value"
									single-line
									clearable
									filled
									dense
									outlined
									hide-details></v-select>
							</div>
						</template>
						<div class="ml-3">
							<v-chip>{{ totalUsers | numeric }}</v-chip>
						</div>
					</v-toolbar>
				</template>

				<template #item.first_name="{ item }">
					{{ item.name }}
				</template>

				<template #item.email="{ item }">
					<a :href="'mailto:' + item.email">{{ item.email }}</a>
				</template>

				<template #item.phone="{ item }">
					<span v-if="item.phone">{{ item.phone }}</span>
					<span v-else class="grey--text">--</span>
				</template>

				<template #item.type="{ item }">
					{{ item.type ? item.type.name : '--' }}
				</template>

				<template #item.client="{ item }">
					<Link v-if="item.client" :href="route('clients.show', item.client)">{{ item.client.name }}</Link>
					<span v-else>n/a</span>
				</template>

				<template #item.last_login="{ item }">
					{{ item.last_login }}
				</template>

				<template #item.is_active="{ item }">
					<v-icon v-if="! item.is_active" color="red">mdi-window-close</v-icon>
					<v-icon v-else color="success">mdi-check</v-icon>
				</template>

				<template #item.actions="{ item }">
					<td class="actions text-end">
						<edit-user icon :user="item" @success="getUsers" />

						<delete-button small icon model="users" :model-id="item.id">
							<v-icon color="danger">
								mdi-delete
							</v-icon>
						</delete-button>
					</td>
				</template>
			</v-data-table>
		</v-card>
	</v-sheet>
</template>
<script>
import _ from 'lodash'
import DeleteButton from '@/Pages/Backend/Components/DeleteButton'
import EditUser from '@/Pages/Backend/Users/Edit'

export default {
	components: { EditUser, DeleteButton },
	props: {
		title: {
			type: String,
			default: 'Users'
		},
		users: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		simplify: {
			type: Boolean,
			default: () => false
		},
		titleBar: {
			type: Boolean,
			default: () => false
		}
	},
	data () {
		return {
			filters: {
				search: '',
				type: '',
				status: '',
				paginated: 30,
				page: 1,
				descending: false,
				sort: 'name'
			},
			types: [],
			statuses: [
				{
					name: 'Active',
					value: true
				},
				{
					name: 'Inactive',
					value: false
				}
			],
			loading: true,
			items: [],
			tableHeaders: [
				{
					text: 'Name',
					align: 'start',
					value: 'first_name'
				},
				{
					text: 'Email',
					align: 'start',
					value: 'email'
				},
				{
					text: 'Phone',
					align: 'start',
					value: 'phone'
				},
				{
					text: 'Type',
					align: 'start',
					value: 'type'
				},
				{
					text: 'Client',
					sortable: false,
					align: 'start',
					value: 'client'
				},
				{
					text: 'Last Login',
					align: 'start',
					value: 'last_login'
				},
				{
					text: 'Active',
					align: 'center',
					value: 'is_active'
				},
				{
					text: '',
					sortable: false,
					align: 'center',
					value: 'actions'
				}
			]
		}
	},
	computed: {
		totalUsers () {
			if (this.users && this.users.hasOwnProperty('meta')) {
				return this.users.meta.total
			}

			return 0
		}
	},
	watch: {
		'filters.search': {
			handler: _.debounce(function (newVal, oldVal) {
				if (newVal && newVal !== oldVal || !newVal && oldVal) {
					this.getUsers()
				}
			}, 350)
		},

		users () {
			if (this.users.hasOwnProperty('data')) {
				this.items = this.users.data
				this.loading = false
			} else {
				this.items = []
			}
		}
	},
	mounted () {
		this.getUserTypes()

		if (this.users.hasOwnProperty('data')) {
			this.items = this.users.data
			this.loading = false
		}

		if (this.$page.props.filters) {
			this.filters = this.$page.props.filters
		}

		this.$watch(vm => [vm.filters.paginated, vm.filters.page, vm.filters.sort, vm.filters.type, vm.filters.status, vm.filters.descending], val => {
			this.getUsers()
		})

		if (this.simplify) {
			// Remove some default columns
			this.tableHeaders = _.remove(this.tableHeaders, header => !['type', 'client'].includes(header.value))
		}
	},
	methods: {
		getUsers () {
			this.$inertia.reload({
				onStart: () => this.loading = true,
				onFinish: () => {
					this.loading = false

					if(! this.length(this.items) && this.filters.page > 1) {
						this.filters.page = 1
					}
				},
				only: ['users'],
				data: this.filters
			})
		}
	}
}
</script>
